/*
 * Première mise en service d'une cible ou d'une carabine, par USB.
 * Écran « Techniciens BZØØK ».
 *
 * DIFFÉRENCE AVEC LES DEUX AUTRES FLUX
 *
 * L'OTA en BLE et l'écran Master ne réécrivent que l'application (0x10000) :
 * ils supposent une carte déjà en service. Une puce neuve n'a rien — ni
 * bootloader, ni table de partitions, ni otadata. Elle ne démarrerait pas. On
 * écrit donc quatre images d'un coup.
 *
 * POURQUOI LE CHOIX CIBLE/CARABINE COMPTE VRAIMENT
 *
 * Il ne sélectionne pas seulement le dossier de firmware : il sélectionne la
 * table de partitions. Une cible réserve 0x1D0000 à app0, une carabine
 * 0x190000. Graver la table de la carabine sur une cible donnerait une app0
 * trop petite pour son firmware de 1,7 Mo. D'où le choix explicite, et pas de
 * détection automatique : sur une puce vierge, il n'y a rien à détecter.
 *
 * Le bootloader et otadata sont, eux, identiques d'un type à l'autre (même
 * carte, même framework), mais rangés par type pour que le jour où l'un des
 * deux projets change de framework, la séparation soit déjà en place.
 */

import React, { useState, useEffect, useRef } from "react";
import {
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonCardSubtitle,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonText,
  IonGrid,
  IonRow,
  IonItem,
  IonCheckbox,
} from "@ionic/react";
import { Transport, IEspLoaderTerminal } from "esptool-js";
import { ouvrirChargeur } from "./chargeurEsp";
import { demanderPortBzook } from "../portSerie";
import PilotesUsb from "./pilotesUsb";
import NvsTechnicien from "./nvsTechnicien";
import ProgressBar from "@ramonak/react-progress-bar";
import { extractVersion, listerFirmwares } from "./versions";

type TypeAppareil = "BZBOX" | "KARABINE";

const APPAREILS: { code: TypeAppareil; libelle: string }[] = [
  { code: "BZBOX", libelle: "Cible" },
  { code: "KARABINE", libelle: "Carabine" },
];

const URL_BINAIRES = (type: TypeAppareil) =>
  `https://ota.bzook.fr/binaires/${type}/`;

// Fichiers d'amorçage embarqués dans l'application (public/assets/amorce/),
// rafraîchis depuis les dépôts firmware par `npm run amorce`.
const URL_AMORCE = (type: TypeAppareil, fichier: string) =>
  `${process.env.PUBLIC_URL}/assets/amorce/${type}/${fichier}`;

// Plan mémoire d'un ESP32 sous Arduino. L'ordre est celui de l'écriture :
// l'application, la plus grosse, passe en dernier.
const ADRESSES = {
  bootloader: 0x1000,
  partitions: 0x8000,
  otadata: 0xe000,
  application: 0x10000,
};


type Etat = "repos" | "encours" | "ok" | "erreur";

/*
 * Avertissement plein écran, partagé par le sas d'entrée et par la
 * confirmation qui précède l'écriture : c'est le même danger, il doit avoir le
 * même visage. Le bouton de refus vient en premier et en plein ; continuer
 * demande de viser un simple contour.
 */
const PleinePage: React.FC<{
  titre: string;
  soustitre: string;
  libelleRetour: string;
  onRetour: () => void;
  libelleAction: string;
  onAction: (ev?: React.MouseEvent) => void;
  children: React.ReactNode;
}> = ({
  titre,
  soustitre,
  libelleRetour,
  onRetour,
  libelleAction,
  onAction,
  children,
}) => (
  <IonContent className="ota-garde">
    <div className="ota-garde-bande" />
    <div className="ota-garde-corps">
      <div className="ota-garde-pictogramme">⚠️</div>
      <h2 className="ota-garde-titre">{titre}</h2>
      <p className="ota-garde-soustitre">{soustitre}</p>
      <div className="ota-garde-detail">{children}</div>
      <div className="ota-garde-actions">
        <IonButton expand="block" color="medium" onClick={onRetour}>
          {libelleRetour}
        </IonButton>
        <IonButton
          expand="block"
          fill="outline"
          className="ota-garde-continuer"
          onClick={onAction}
        >
          {libelleAction}
        </IonButton>
      </div>
    </div>
    <div className="ota-garde-bande" />
  </IonContent>
);

/* Les outils de l'environnement technicien. « Mise en service » reprogramme une carte
   neuve ; « Pilotes USB » dépanne le cas où la carte n'apparaît même pas dans la fenêtre
   de choix du port. Ce second écran vivait dans la barre principale jusqu'à la V2.7 : il
   n'y avait rien à y faire, personne n'installe un pilote en cours de séance. */
type Outil = "service" | "pilotes" | "nvs";

const FlashUsine: React.FC<{ onFermer: () => void }> = ({ onFermer }) => {
  const [outil, setOutil] = useState<Outil>("service");
  // Deux passages obligés, parce que les deux erreurs possibles n'arrivent pas
  // au même moment : entrer ici sans être technicien, et se tromper de type
  // juste avant d'écrire.
  const [acces, setAcces] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [type, setType] = useState<TypeAppareil | null>(null);
  const [fichiers, setFichiers] = useState<string[]>([]);
  const [fichier, setFichier] = useState<string | null>(null);
  const [effacerTout, setEffacerTout] = useState(true);
  const [etat, setEtat] = useState<Etat>("repos");
  const [message, setMessage] = useState<string>("");
  const [progression, setProgression] = useState(0);
  const [journal, setJournal] = useState<string[]>([]);

  const finJournal = useRef<HTMLDivElement | null>(null);
  const serieDisponible =
    typeof navigator !== "undefined" && "serial" in navigator;

  const journaliser = (ligne: string) =>
    setJournal((j) => [...j, ligne].slice(-300));

  const terminal = useRef<IEspLoaderTerminal>({
    clean: () => setJournal([]),
    writeLine: (donnee: string) => setJournal((j) => [...j, donnee].slice(-300)),
    write: (donnee: string) =>
      setJournal((j) => {
        const copie = j.length ? [...j] : [""];
        copie[copie.length - 1] += donnee;
        return copie.slice(-300);
      }),
  }).current;

  // Le type choisi commande le dossier interrogé : la liste ne contient donc
  // que des versions compatibles, la plus récente présélectionnée.
  useEffect(() => {
    if (!type) return;
    setFichiers([]);
    setFichier(null);
    setMessage("");
    listerFirmwares(URL_BINAIRES(type))
      .then((liste) => {
        setFichiers(liste);
        const derniere = liste.find((f) => extractVersion(f));
        if (derniere) setFichier(derniere);
        else setMessage("Aucun firmware trouvé dans " + URL_BINAIRES(type));
      })
      .catch((e) =>
        setMessage("Listing des firmwares impossible : " + e.message)
      );
  }, [type]);

  useEffect(() => {
    finJournal.current?.scrollIntoView({ block: "end" });
  }, [journal]);

  const telecharger = async (url: string): Promise<Uint8Array> => {
    const reponse = await fetch(url);
    if (!reponse.ok) throw new Error(`${url} : HTTP ${reponse.status}`);
    return new Uint8Array(await reponse.arrayBuffer());
  };

  const flasher = async (ev?: React.MouseEvent) => {
    if (!type || !fichier) return;

    // Lu avant tout await : React recycle l'événement.
    const sansFiltre = ev?.shiftKey === true;

    setConfirmation(false);
    setEtat("encours");
    setProgression(0);
    setMessage("");
    setJournal([]);

    let transport: Transport | null = null;
    const debut = Date.now();

    try {
      // Tout est récupéré AVANT d'ouvrir le port : si le réseau lâche, la carte
      // n'a pas encore été basculée en bootloader ni effacée.
      journaliser("Récupération des fichiers…");
      const [bootloader, partitions, otadata, application] = await Promise.all([
        telecharger(URL_AMORCE(type, "bootloader.bin")),
        telecharger(URL_AMORCE(type, "partitions.bin")),
        telecharger(URL_AMORCE(type, "boot_app0.bin")),
        telecharger(`${URL_BINAIRES(type)}${fichier}`),
      ]);

      const images = [
        { nom: "bootloader.bin", data: bootloader, address: ADRESSES.bootloader },
        { nom: "partitions.bin", data: partitions, address: ADRESSES.partitions },
        { nom: "boot_app0.bin", data: otadata, address: ADRESSES.otadata },
        { nom: fichier, data: application, address: ADRESSES.application },
      ];
      images.forEach((i) =>
        journaliser(
          `  0x${i.address.toString(16).padStart(5, "0")}  ${i.nom} — ${(
            i.data.length / 1024
          ).toFixed(0)} Ko`
        )
      );

      // requestPort() exige un geste utilisateur : on est bien dans un onClick
      if (sansFiltre) { journaliser("Maj + clic : tous les ports série sont listés."); }
      const port = await demanderPortBzook(sansFiltre);
      transport = new Transport(port, true);

      const { loader, puce } = await ouvrirChargeur(
        transport,
        terminal,
        journaliser
      );
      journaliser(`Puce détectée : ${puce}`);

      // esptool-js rend la progression image par image. On la ramène à une
      // seule barre, sinon elle repartirait de zéro quatre fois.
      const total = images.reduce((s, i) => s + i.data.length, 0);
      const cumulAvant = images.map((_, i) =>
        images.slice(0, i).reduce((s, x) => s + x.data.length, 0)
      );

      await loader.writeFlash({
        fileArray: images.map(({ data, address }) => ({ data, address })),
        flashMode: "dio",
        flashFreq: "40m",
        flashSize: "4MB",
        // Une carte neuve peut sortir d'un test, ou avoir servi à l'autre type
        // d'appareil : l'effacement garantit une NVS vierge (appairage,
        // compteur de brownout) plutôt qu'un mélange indétectable.
        eraseAll: effacerTout,
        compress: true,
        reportProgress: (index, ecrit) => {
          setProgression(
            Math.round(((cumulAvant[index] + ecrit) / total) * 100)
          );
        },
      });

      await loader.after("hard_reset");

      const duree = Math.round((Date.now() - debut) / 1000);
      setProgression(100);
      setEtat("ok");
      setMessage(
        `${
          type === "BZBOX" ? "Cible" : "Carabine"
        } mise en service en ${duree} s (version ${extractVersion(fichier)}). ` +
          `Elle redémarre seule ; les mises à jour suivantes se feront en BLE.`
      );
    } catch (e: any) {
      const detail = e?.message ?? String(e);
      setEtat("erreur");
      setMessage(detail);
      journaliser(`❌ ${detail}`);
    } finally {
      try {
        await transport?.disconnect();
      } catch (e) {
        /* port déjà fermé */
      }
    }
  };

  const enCours = etat === "encours";
  const versionCible = extractVersion(fichier);
  const libelleType = type === "BZBOX" ? "Cible" : "Carabine";

  // Sas d'entrée : on ne tombe pas sur cet écran, on y entre exprès.
  if (!acces) {
    return (
      <PleinePage
        titre="Réservé aux techniciens BZØØK"
        soustitre="Cet écran reprogramme une carte à zéro. Ce n'est pas une mise à jour."
        libelleRetour="Retour — je veux mettre à jour un appareil"
        onRetour={onFermer}
        libelleAction="Je suis technicien BZØØK — continuer"
        onAction={() => setAcces(true)}
      >
        <ul>
          <li>
            Il écrit le <strong>bootloader</strong> et la{" "}
            <strong>table de partitions</strong>, ce qu'aucun autre écran ne
            fait. Une carte déjà en service n'en a pas besoin.
          </li>
          <li>
            Le choix <strong>Cible ou Carabine</strong> décide de la table
            gravée. Se tromper rend la carte inutilisable, sans message
            d'erreur.
          </li>
          <li>
            La mémoire est <strong>effacée</strong> par défaut : appairage et
            réglages sont perdus.
          </li>
          <li>
            Pour mettre à jour un appareil qui fonctionne, revenez en arrière et
            utilisez <strong>Scan</strong>.
          </li>
        </ul>
      </PleinePage>
    );
  }

  // Dernier arrêt avant écriture : le seul moment où l'on peut encore
  // rattraper une erreur de type, une fois le choix fait et le firmware connu.
  if (confirmation && type && fichier) {
    return (
      <PleinePage
        titre="Dernière vérification"
        soustitre="L'écriture va commencer. Vérifiez ce qui est branché."
        libelleRetour="Annuler"
        onRetour={() => setConfirmation(false)}
        libelleAction={`Écrire sur la ${libelleType.toLowerCase()}`}
        onAction={flasher}
      >
        <ul>
          <li>
            Carte branchée : <strong>{libelleType}</strong>. Si ce n'en est pas
            une, annulez — la table de partitions gravée lui serait inadaptée.
          </li>
          <li>
            Firmware : <strong>{fichier}</strong> (version {versionCible}).
          </li>
          <li>
            Mémoire :{" "}
            <strong>
              {effacerTout
                ? "entièrement effacée avant écriture"
                : "conservée (appairage et réglages gardés)"}
            </strong>
            .
          </li>
          <li>
            Une seule carte doit être branchée en USB. L'opération dure environ
            une minute et ne doit pas être interrompue.
          </li>
        </ul>
      </PleinePage>
    );
  }

  return (
    <IonContent class="ion-padding">
      {/* Bandeau collant : tant qu'on est dans cet écran, on doit le savoir. */}
      <div className="ota-bandeau-technicien">
        <div className="ota-bandeau-rayures" />
        <div className="ota-bandeau-texte">
          ⚠️ MODE TECHNICIEN — reprogrammation complète d'une carte neuve. Pour
          mettre à jour un appareil en service, utilisez « Scan ».
        </div>
      </div>

      <IonSegment
        value={outil}
        disabled={enCours}
        onIonChange={(e) => setOutil(e.detail.value as Outil)}
      >
        <IonSegmentButton value="service">
          <IonLabel>Mise en service</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="pilotes">
          <IonLabel>Pilotes USB</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="nvs">
          <IonLabel>Réglages NVS</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      {outil === "pilotes" && <PilotesUsb onFermer={onFermer} />}

      {outil === "nvs" && <NvsTechnicien />}

      {outil === "service" && (
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Techniciens BZØØK — mise en service</IonCardTitle>
          <IonCardSubtitle>
            Première programmation d'une carte neuve, par USB : bootloader,
            table de partitions et firmware. À réserver à une carte qui n'a
            jamais servi, ou à remettre à zéro — un appareil déjà en service se
            met à jour par le Scan BLE.
          </IonCardSubtitle>
        </IonCardHeader>

        <IonCardContent>
          {!serieDisponible && (
            <IonText color="danger">
              <p>
                Ce navigateur n'expose pas le port série. Utilisez le BZØØK
                Booster, ou Chrome / Edge sur ordinateur.
              </p>
            </IonText>
          )}

          {/* Aucune détection possible : une puce vierge ne dit pas ce qu'elle
              va devenir. C'est le technicien qui tranche, et ce choix commande
              la table de partitions gravée. */}
          <IonSegment
            value={type ?? undefined}
            disabled={enCours}
            onIonChange={(e) => setType(e.detail.value as TypeAppareil)}
          >
            {APPAREILS.map((a) => (
              <IonSegmentButton key={a.code} value={a.code}>
                <IonLabel>{a.libelle}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>

          {type && (
            <>
              <IonSelect
                value={fichier}
                placeholder="Choisir un firmware"
                disabled={enCours || fichiers.length === 0}
                onIonChange={(e) => setFichier(e.detail.value)}
              >
                {fichiers.map((f) => (
                  <IonSelectOption key={f} value={f}>
                    {f}
                  </IonSelectOption>
                ))}
              </IonSelect>

              {versionCible && (
                <IonText>
                  <p className="ota-target-version">
                    🎯 Version cible : {versionCible}
                    {fichier === fichiers[0] && " (dernière disponible)"}
                  </p>
                </IonText>
              )}

              <IonItem lines="none">
                <IonCheckbox
                  slot="start"
                  checked={effacerTout}
                  disabled={enCours}
                  onIonChange={(e) => setEffacerTout(e.detail.checked)}
                />
                <IonLabel class="ion-text-wrap">
                  Effacer toute la mémoire d'abord (appairage, réglages)
                </IonLabel>
              </IonItem>

              <IonButton
                onClick={() => setConfirmation(true)}
                disabled={!fichier || enCours || !serieDisponible}
              >
                {enCours ? "Programmation…" : "Mettre en service"}
              </IonButton>
            </>
          )}

          <IonButton color="medium" onClick={onFermer} disabled={enCours}>
            Retour
          </IonButton>

          {(enCours || progression > 0) && (
            <ProgressBar
              completed={progression}
              labelAlignment="center"
              bgColor="#3880ff"
            />
          )}

          {message && (
            <IonText color={etat === "erreur" ? "danger" : "success"}>
              <p>{message}</p>
            </IonText>
          )}

          {enCours && (
            <IonText color="warning">
              <p>
                Ne débranchez pas la carte. Une coupure en cours d'écriture la
                laisse muette, mais jamais perdue : le bootloader de l'ESP32 est
                gravé en usine, il suffit de recommencer.
              </p>
            </IonText>
          )}
        </IonCardContent>
      </IonCard>
      )}

      {outil === "service" && journal.length > 0 && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Journal</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <pre className="ota-journal">
                  {journal.join("\n")}
                  <div ref={finJournal} />
                </pre>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>
      )}
    </IonContent>
  );
};

export default FlashUsine;
